/*
 * Search: the term, the order, the two empty states and the hits. A search runs on Enter and never
 * while typing (A26), and the term is never in the URL — the copy below is written for a screen
 * whose whole state is what somebody just typed, and whose one field says what can be typed into it.
 */

export const en = {
	"search.title": "Search",
	"search.subtitle":
		"Everything you can see — messages, the drive, code and the people who wrote them.",
	// The lead of a reader the code search is not for (a guest): only the messages half is asked.
	"search.subtitle.messages": "Everything you can see — messages and the people who wrote them.",
	// The label is drawn for a screen reader only: the glyph in the box and the placeholder already say
	// what the one field of this screen is for, and a heading over it said it a third time (X-B1 P1).
	"search.query.label": "Search term",
	// The examples are the screen's own chips, built from the reader's world (SearchPage.tsx), so the
	// placeholder and the chips say the same thing and name nobody who is not here (X-B4 B4-14).
	"search.query.placeholder": "Search everything — try {examples}",
	"search.order.label": "Order",
	"search.order.recent": "Newest",
	"search.order.best": "Best match",

	"search.idle": "Type something and press Enter.",
	// ---- K-04: the screen before the first search ----
	"search.examples.label": "Examples",
	"search.none.lead": "Try fewer words, or take a filter off.",
	"search.recent.label": "Recent searches",
	"search.running": "Searching …",
	// The term is quoted: a search for "and" would otherwise run into the sentence around it.
	"search.count": "{count} results for “{term}”",
	"search.count.one": "{count} result for “{term}”",
	"search.countCapped": "More than {count} results for “{term}”",
	// "More than 25" with no *Show more* under it is a screen promising something it cannot do: the
	// substring fallback answers one page and hands back no cursor, so the rest is unreachable from
	// here and the sentence has to say so, with the two things that do reach it.
	"search.countCapped.noMore":
		"Only the first {count} are shown, and there is no page after them. Try a longer word, or add a filter.",
	"search.none": "Nothing found for “{term}”.",
	"search.none.everywhere": "Search everywhere",
	// Two things the server has always said about *how* it searched and which the screen dropped.
	// Without the first, a list of hits that do not contain the word as it was typed looks like a
	// broken search; without the second, a pass that gave up before the end is drawn as "Nothing
	// found", which is the interface asserting something the server did not say.
	"search.fallback":
		"No message uses “{term}” as a word of its own — these have it inside a longer one.",
	"search.truncated.title": "The search stopped before it reached the end.",
	"search.truncated.lead":
		"Older messages were not looked at. Try again, or search for a longer word.",
	// The instance cannot search — not the same thing as having looked and found nothing.
	"search.unavailable": "Search is not available on this instance.",
	// Where a hit is, when nothing can name the conversation: not in the reader's model and not in the
	// public directory either, which leaves a private conversation they are not in — the one case the
	// sentence was ever true of (X-B4 B4-9).
	"search.group.unknown": "A conversation you are not in",
	// A hit whose snippet is empty — a message that is only a code block, whose text the snippet
	// builder does not cut from — still has to be a link somebody can see and a name a screen
	// reader can read out.
	"search.hit.noSnippet": "A message with no text to show",
	"search.hit.open": "Open this message",
	"search.hit.view": "View in conversation",
	"search.hit.hasFile": "Has an attachment",
	// Why a row with no marked run is a hit at all: the query matched the name of a file on it. Only
	// drawn for a message the client holds — an unloaded one keeps the pill alone (X-B4 B4-15).
	"search.hit.files": "Attached: {names}",
	"search.hit.notJoined": "Not joined",
	"search.more": "Show more results",

	/*
	 * ---- S-07: the repositories' docs and code ----
	 *
	 * One box answers three questions, so the answer is three sections — Messages, Documents, Code —
	 * and the second two are one scan of the repositories this instance has already fetched. What the
	 * copy has to carry beyond the rows themselves is everything the scan could not do: a repository
	 * with no clone, a repository that failed, a cap that cut the answer, the half-hour-old tip, and
	 * the one class of file that is never searched at all.
	 */
	"search.group.messages": "Messages",
	// The repositories' pages, which the scan finds: "Documents" alone now reads as the drive's.
	"search.group.documents": "Docs in repositories",
	"search.group.code": "Code",
	"search.group.files": "{count} files",
	"search.group.files.one": "1 file",
	"search.group.filesCapped": "More than {count} files",
	"search.code.none": "Nothing in the repositories.",
	"search.code.matches": "{count} matches",
	"search.code.matches.one": "1 match",
	"search.code.matchesCapped": "{count}+ matches",
	// A file whose *name* holds the word and whose text does not: that is how a search for
	// "protocol.md" finds the page, which no text search can, and the row says so instead of
	// standing there with no excerpt under it.
	"search.code.nameMatch": "The name of this file matches.",
	"search.code.matchCase": "Match case",
	"search.code.wholeWords": "Whole words",
	// The legend over the two switches. Without it they read as switches of the whole screen, and
	// they are not: the message search folds case and diacritics always (normalize.go), and only the
	// scan over the repositories matches the bytes as they were typed.
	"search.code.switches": "In the repositories",
	"search.code.more": "Show more files",
	// One pill on each section's heading rather than one on each of its rows: every hit opens the
	// code file page, which reads and never edits, and saying so twenty times is not saying it
	// better. The file page carries the same pill beside its heading.
	"search.code.readOnly": "Read only",
	// The code file page (core/search/CodeFilePage.tsx): one tracked file at the commit a hit was
	// found at, with the hit's line marked. The heading is the path; this line says where it is from.
	"search.code.file.where": "In {repo}, as committed at {commit}",
	"search.code.file.back": "Back to the search",
	"search.code.file.loading": "Reading the file…",
	// A file whose text came whole from the server but is longer than the page draws: the page cuts
	// it on a line, and says so above the lines.
	"search.code.file.cut": "Only the first megabyte of this file is shown.",
	// A file the server named and did not send, because not even the first megabyte would be honest
	// without the rest. {size} is the size in the reader's own units (formatBytes).
	"search.code.file.tooBig": "This file is {size}, too large to show here.",
	// The accessible name of the numbered list of lines.
	"search.code.file.lines": "The lines of {path}",
	// What a file is, where it is not ordinary source or an ordinary page. The three words are what
	// the ranking already orders by, and a reader who sees a hit from a vendored copy at the bottom
	// of the list should be able to tell at a glance why it is there.
	"search.code.class.generated": "Generated",
	"search.code.class.vendored": "From elsewhere",
	"search.code.class.unlisted": "Not the main code",
	"search.code.excluded": "Keys and certificates are never searched.",
	"search.code.truncated": "The search stopped early, so this is not all of it. Try a longer word.",
	"search.code.stale": "The repositories were last updated at {time}.",
	"search.code.repoMissing": "The {repo} project is not set up yet and was not searched.",
	"search.code.repoFailed": "The {repo} project could not be searched.",
	// The one refusal with no sentence from the server: an instance whose build has no such route.
	// Every other one — a pattern that cannot be searched for, a repository nobody has, a stale
	// page, the in-flight limit, no clone at all — answers `error.search.code.*`, whose English is
	// in core's own catalogue, and the section renders what the server said.
	"search.code.unavailable": "The repositories cannot be searched right now.",
	// The first page pinned the version every repository was searched at, and one of them is gone —
	// somebody rewrote a branch. These rows are real; they are not cut from the same corpus.
	"search.code.refreshed":
		"These files moved on while you were reading, so this page comes from the newest version.",

	/*
	 * ---- DV S-09 §3.3: the Drive section ----
	 *
	 * One card per item of the drive: its name, where it lives, who changed it last and when, and up
	 * to three places in its text, each "in" the heading path it stands under.
	 */
	"search.group.drive": "Drive",
	"search.drive.none": "Nothing in Drive matches “{q}”.",
	"search.drive.all": "Search all of teamBase",
	// The accessible name of a place in a document's text: "In Pricing › Plans: three plans …".
	"search.drive.section": "In {section}",
	"search.drive.nameMatch": "matches the name",
	"search.drive.places": "{count} places",
	"search.drive.places.one": "1 place",
	"search.drive.moreSections": "{count} more places in this document",
	"search.drive.moreSections.one": "1 more place in this document",
	// A document, file or folder linked in the text is drawn as a small link icon in an excerpt: its
	// title is the reader's own, and the index holds no word of it (S-09 D21).
	"search.drive.linked": "Linked item",
	// The deleted group (DS-3 §3.3): what went to the trash in the last year, after every live card,
	// collapsed into one line while live cards stand above it. The caveat is said only here, and only
	// when something deleted matched.
	"search.drive.deleted.more": "{count} deleted items also match",
	"search.drive.deleted.more.one": "1 deleted item also matches",
	"search.drive.deleted.moreCapped": "More than {count} deleted items also match",
	"search.drive.deleted.show": "Show",
	"search.drive.deleted.showNamed": "{line} — Show",
	"search.drive.deleted.hide": "Hide",
	"search.drive.deleted.title": "Deleted items",
	"search.drive.deleted.caveat": "Moved to the trash in the last year. They may be out of date.",
	"search.drive.deleted.mark": "Deleted · {when} · {who}",
	"search.drive.deleted.markClaude": "Deleted · {when} · Claude, asked by {person}",
	"search.drive.deleted.trash": "Search the trash",

	// V-04 — the filter bar. A chip says what the server did with one operator, so the operator keeps
	// the spelling it is typed in; the three menus and the toggle write the query rather than holding
	// state of their own, which is why each is named after the filter it sets.
	"search.filter.asText": "Searched for “{text}” as text",
	"search.filter.chip": "{operator}: {value}",
	// A control that holds its filter says so in the reader's words, because it is the one place that
	// filter is drawn: the chip is the filter (X-B1 D11).
	"search.filter.chosen.after": "Since {value}",
	"search.filter.chosen.before": "Until {value}",
	"search.filter.chosen.from": "From {value}",
	"search.filter.chosen.has": "Has {value}",
	"search.filter.chosen.in": "In {value}",
	"search.filter.chosen.on": "On {value}",
	"search.filter.chosen.path": "Under {value}",
	// A kind of item's own word is the whole sentence: "Documents".
	"search.filter.chosen.type": "{value}",
	"search.filter.chosen.repo": "In {value}",
	"search.filter.file": "a file",
	"search.filter.from": "From anyone",
	"search.filter.hasFile": "Has a file",
	"search.filter.in": "In any conversation",
	"search.filter.noPerson": "nobody here has that handle",
	"search.filter.noPlace": "no conversation you can read is called that",
	"search.filter.remove": "Remove the filter {operator}: {value}",
	"search.filter.time": "Any time",
	"search.filter.time.month": "In the last 30 days",
	"search.filter.time.today": "Today",
	"search.filter.time.week": "In the last 7 days",
	// The drive's controls (S-01 §3.15). Narrowed to the Drive section, the person and the day read as
	// what they are of an item: who changed it, and when.
	"search.filter.type": "Type",
	"search.filter.type.document": "Documents",
	"search.filter.type.file": "Files",
	"search.filter.type.folder": "Folders",
	"search.filter.changedBy": "Changed by",
	"search.filter.modified": "Modified",
	"search.filter.space": "Shared drive",
	"search.filter.driveOnly": "Drive only",
	"search.filter.noDrive": "no shared drive you can open has that key",
} as const satisfies Record<string, string>
